import type {
  PoseLandmarks,
  CalibrationResult,
  ReferenceObjectType,
} from '@/types/measurements';

/** MediaPipe landmark indices */
const NOSE = 0;
const LEFT_EYE = 1;
const RIGHT_EYE = 2;
const LEFT_ANKLE = 27;
const RIGHT_ANKLE = 28;

export class CalibrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CalibrationError';
  }
}

/**
 * Calibrate pixel-to-cm scale using the user's known height and detected landmarks.
 *
 * Estimates head top position from nose/eye landmarks, measures body height in pixels
 * from head top to ankle midpoint, then computes pixels-per-cm.
 */
export function calibrateFromHeight(
  landmarks: PoseLandmarks,
  userHeightCm: number,
  imageHeight: number
): CalibrationResult {
  // Input validation
  if (!Number.isFinite(userHeightCm)) {
    throw new CalibrationError('userHeightCm must be a finite number');
  }
  if (userHeightCm < 50 || userHeightCm > 250) {
    throw new CalibrationError(
      `userHeightCm (${userHeightCm}) must be between 50 and 250 cm`
    );
  }
  if (!Number.isFinite(imageHeight) || imageHeight <= 0) {
    throw new CalibrationError('imageHeight must be a positive number');
  }
  if (!landmarks || landmarks.length < 33) {
    throw new CalibrationError('Landmarks array must contain 33 landmarks');
  }

  // Check required landmark visibility
  const nose = landmarks[NOSE]!;
  const leftAnkle = landmarks[LEFT_ANKLE]!;
  const rightAnkle = landmarks[RIGHT_ANKLE]!;

  if (nose.visibility < 0.3) {
    throw new CalibrationError(
      'Nose landmark not visible enough for calibration (visibility < 0.3)'
    );
  }

  const bestAnkle =
    leftAnkle.visibility >= rightAnkle.visibility ? leftAnkle : rightAnkle;
  if (bestAnkle.visibility < 0.3) {
    throw new CalibrationError(
      'No ankle landmark visible enough for calibration (visibility < 0.3)'
    );
  }

  // Estimate head top position
  // Head top is approximately 8% of body height above the nose
  const leftEye = landmarks[LEFT_EYE]!;
  const rightEye = landmarks[RIGHT_EYE]!;
  const eyeMidY = (leftEye.y + rightEye.y) / 2;
  const noseToEyeDist = Math.abs(nose.y - eyeMidY);

  // Head top ≈ 3× the nose-to-eye distance above the nose
  const headTopY = nose.y - noseToEyeDist * 3;

  // Ankle midpoint (use the more visible ankle, or average both if both visible)
  let ankleY: number;
  if (leftAnkle.visibility > 0.3 && rightAnkle.visibility > 0.3) {
    ankleY = (leftAnkle.y + rightAnkle.y) / 2;
  } else {
    ankleY = bestAnkle.y;
  }

  // Body height in pixels (landmarks are normalized [0,1], multiply by imageHeight)
  const bodyHeightPx = (ankleY - headTopY) * imageHeight;

  // Divide-by-zero guard
  if (bodyHeightPx < 10) {
    throw new CalibrationError(
      `Detected body height too small (${bodyHeightPx.toFixed(1)}px). Ensure full body is visible in the photo.`
    );
  }

  const pixelsPerCm = bodyHeightPx / userHeightCm;

  return {
    pixelsPerCm,
    method: 'manual_height' as ReferenceObjectType,
    confidence: 0.85,
  };
}

/**
 * Calibrate pixel-to-cm scale using a reference object in the image.
 * NOT YET IMPLEMENTED - deferred to future phase.
 *
 * Per research findings, object detection (A4 paper, credit card) is unreliable
 * in poor lighting conditions. Height-based calibration is the primary method.
 */
export function calibrateFromObject(
  _imageElement: HTMLImageElement,
  _objectType: ReferenceObjectType
): CalibrationResult {
  throw new Error(
    'Object calibration not yet implemented. Use height-based calibration.'
  );
}
